/**
 * MiniResourceTimeline
 *
 * Vertical list of the 20 most recent resource-board assignments.
 *
 * Real-time updates:
 *  - Socket.IO: joins the same 'resource-board' room as ResourceBoard.
 *    On 'board:refresh' (emitted after every save) → silent re-fetch.
 *  - Polling fallback every POLL_MS in case the socket server is down.
 *  - Page Visibility API: pauses both when tab is hidden, resumes on show.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { RESOURCE_BOARD_CONFIG } from '../../daily-task-coordination/utils/config';
import {
  Loader2, CalendarDays, RefreshCw,
  PlaneTakeoff, PlaneLanding,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';
const POLL_MS  = 10_000;  // 10 s — fast enough to feel live when socket isn't available

const FETCH_ASGN  = 50;
const FETCH_TRVL  = 50;
const MATCH_DAYS  = 4;
const WIN_PAST    = 30;  // days before today to include (catches ongoing assignments)
const WIN_FUTURE  = 60;  // days after today to include (upcoming)

const isoDate = d => d.toISOString().slice(0, 10);

// ─── pure helpers ─────────────────────────────────────────────────────────────

const diffDays = (a, b) => Math.round((a - b) / 86400000);

const fmtDate = s => {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};

const fmtTime = d => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

const FALLBACK_COLORS = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#EC4899','#14B8A6','#F97316'];

const assignColor = item => {
  if (item.hex_color)           return item.hex_color;
  if (item.project_data?.color) return item.project_data.color;
  return FALLBACK_COLORS[(item.project || 0) % FALLBACK_COLORS.length];
};

const hexRgba = (hex, alpha) => {
  const h = (hex || '#3B82F6').replace('#', '');
  return `rgba(${parseInt(h.slice(0,2),16)},${parseInt(h.slice(2,4),16)},${parseInt(h.slice(4,6),16)},${alpha})`;
};

const TRAVEL_COLORS = { check_in: '#F59E0B', check_out: '#F97316' };

// ─── travel matching ──────────────────────────────────────────────────────────

const matchTravelsToAssignments = (assignments, travels) => {
  const matchedIds = new Set();
  const enriched = assignments.map(a => {
    const aStart = new Date(a.start_date); aStart.setHours(0,0,0,0);
    const aEnd   = new Date(a.end_date);   aEnd.setHours(0,0,0,0);
    const forUser = travels.filter(t => t.user_id === a.user);

    const checkIn = forUser
      .filter(t => t.travel_type === 'check_in')
      .find(t => {
        const d = diffDays(aStart, new Date(t.start_date));
        return d >= 0 && d <= MATCH_DAYS;
      }) || null;

    const checkOut = forUser
      .filter(t => t.travel_type === 'check_out')
      .find(t => {
        const d = diffDays(new Date(t.start_date), aEnd);
        return d >= 0 && d <= MATCH_DAYS;
      }) || null;

    if (checkIn)  matchedIds.add(checkIn.id);
    if (checkOut) matchedIds.add(checkOut.id);
    return { ...a, _checkIn: checkIn, _checkOut: checkOut };
  });
  return { enriched, unmatchedTravels: travels.filter(t => !matchedIds.has(t.id)) };
};

// ─── component ────────────────────────────────────────────────────────────────

const MiniResourceTimeline = () => {
  const [assignments,     setAssignments]     = useState([]);
  const [unmatched,       setUnmatched]       = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [lastUpdated,     setLastUpdated]     = useState(null);
  const [refreshing,      setRefreshing]      = useState(false);
  const [socketConnected, setSocketConnected] = useState(false);

  // ── fetch (silent = background, no spinner) ──────────────────────────────

  const fetch = useCallback((silent = false) => {
    if (silent) setRefreshing(true);
    else        { setLoading(true); setError(null); }

    const token   = localStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const today = new Date();
    const winStart = new Date(today); winStart.setDate(today.getDate() - WIN_PAST);
    const winEnd   = new Date(today); winEnd.setDate(today.getDate() + WIN_FUTURE);

    Promise.all([
      axios.get(`${API_BASE}/api/resource-planning-assignments/`, {
        headers,
        params: {
          start_date: isoDate(winStart),
          end_date:   isoDate(winEnd),
          page_size:  FETCH_ASGN,
          ordering:   'start_date',
        },
      }),
      axios.get(`${API_BASE}/api/travel/`, {
        headers,
        params: {
          start_date: isoDate(winStart),
          end_date:   isoDate(winEnd),
          page_size:  FETCH_TRVL,
          ordering:   'start_date',
        },
      }),
    ])
      .then(([aRes, tRes]) => {
        const toArr = r => Array.isArray(r.data) ? r.data : (r.data?.results ?? []);

        const rawAssignments = toArr(aRes);
        const rawTravels = toArr(tRes)
          .filter(t => t.travel_type === 'check_in' || t.travel_type === 'check_out');

        const { enriched, unmatchedTravels } = matchTravelsToAssignments(rawAssignments, rawTravels);

        // Newest first — highest ID (most recently created) at top
        enriched.sort((a, b) => b.id - a.id);

        setAssignments(enriched);
        setUnmatched(unmatchedTravels);
        setLastUpdated(new Date());
        if (!silent) setError(null);
      })
      .catch(err => {
        console.error('MiniResourceTimeline fetch:', err);
        if (!silent) setError('Could not load data.');
      })
      .finally(() => {
        setLoading(false);
        setRefreshing(false);
      });
  }, []);

  // ── Socket.IO — join resource-board room, listen for board:refresh ────────

  useEffect(() => {
    const socket = io(RESOURCE_BOARD_CONFIG.socket.url, {
      reconnectionAttempts: RESOURCE_BOARD_CONFIG.socket.reconnectionAttempts,
      reconnectionDelay:    RESOURCE_BOARD_CONFIG.socket.reconnectionDelay,
      transports: ['websocket', 'polling'],
    });

    socket.on('connect', () => {
      setSocketConnected(true);
      socket.emit('board:join', {
        boardId:  RESOURCE_BOARD_CONFIG.socket.boardId,
        userId:   'dashboard-timeline',
        userName: 'Dashboard',
        color:    '#3B82F6',
      });
    });

    socket.on('disconnect',    () => setSocketConnected(false));
    socket.on('connect_error', () => setSocketConnected(false));

    // After save — data is already committed when this fires
    const silentFetch = () => fetch(true);
    socket.on('board:refresh',          silentFetch);
    // Also listen to individual element events (fired before the full save)
    socket.on('board:element_created',  silentFetch);
    socket.on('board:element_updated',  silentFetch);
    socket.on('board:element_deleted',  silentFetch);

    return () => {
      socket.off('board:refresh',         silentFetch);
      socket.off('board:element_created', silentFetch);
      socket.off('board:element_updated', silentFetch);
      socket.off('board:element_deleted', silentFetch);
      socket.emit('board:leave', {
        boardId: RESOURCE_BOARD_CONFIG.socket.boardId,
        userId:  'dashboard-timeline',
      });
      socket.disconnect();
    };
  }, [fetch]);

  // ── Polling fallback + Page Visibility API ────────────────────────────────

  const socketConnectedRef = useRef(socketConnected);
  useEffect(() => { socketConnectedRef.current = socketConnected; }, [socketConnected]);

  useEffect(() => {
    fetch(false); // initial load

    // Poll less often when socket is live (just a safety net)
    const tick = () => {
      if (!document.hidden) fetch(true);
    };
    const interval = setInterval(tick, POLL_MS);

    const onVisibility = () => { if (!document.hidden) fetch(true); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetch]);

  // ── display helpers ───────────────────────────────────────────────────────

  const userName = item => {
    const u = item.user_data;
    if (!u) return '—';
    return `${u.first_name || ''} ${u.last_name || ''}`.trim() || '—';
  };

  const projLabel = item =>
    item.is_special_tile ? (item.custom_name || '—') : (item.project_data?.name || '—');

  const initials = name =>
    name.split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col bg-white overflow-hidden">

      {/* header */}
      <div className="flex items-center justify-between px-3 py-2 border-b-2 border-brand-500 bg-steel-50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="p-1 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600">
            <CalendarDays size={13} className="text-white" />
          </div>
          <span className="text-base font-semibold text-slate-800">Resource Timeline</span>

          {/* connection indicator */}
          {socketConnected ? (
            <span className="flex items-center gap-1">
              <span
                className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"
                style={{ animation: 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite' }}
              />
              <span className="text-[9px] font-semibold text-emerald-500 uppercase tracking-wide">Live</span>
            </span>
          ) : (
            <span className="flex items-center gap-1" title="Socket disconnected — using polling">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />
              <span className="text-[9px] font-semibold text-amber-500 uppercase tracking-wide">Polling</span>
            </span>
          )}

          {refreshing && <Loader2 size={10} className="animate-spin text-slate-300" />}
        </div>

        <div className="flex items-center gap-2">
          {lastUpdated && (
            <span className="text-[9px] text-slate-400">{fmtTime(lastUpdated)}</span>
          )}
          <button
            onClick={() => fetch(false)}
            title="Refresh now"
            className="p-1 rounded hover:bg-steel-200 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <RefreshCw size={12} />
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 size={20} className="animate-spin text-slate-300" />
        </div>
      )}
      {!loading && error && (
        <div className="flex-1 flex items-center justify-center text-xs text-rose-400 px-4 text-center">
          {error}
        </div>
      )}

      {!loading && !error && assignments.length === 0 && unmatched.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 text-slate-300">
          <CalendarDays size={28} />
          <span className="text-xs">No recent assignments</span>
        </div>
      )}

      {!loading && !error && (assignments.length > 0 || unmatched.length > 0) && (
        <div className="flex-1 min-h-0 flex flex-col">

          <div className="flex items-center gap-2 px-3 py-1 border-b border-steel-100 bg-steel-50 flex-shrink-0">
            <span className="flex-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Person / Project</span>
            <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide w-36 text-center">Dates</span>
          </div>

          <div className="flex-1 overflow-y-auto">

            {assignments.map((item, i) => {
              const color    = assignColor(item);
              const name     = userName(item);
              const proj     = projLabel(item);
              const checkIn  = item._checkIn;
              const checkOut = item._checkOut;

              return (
                <div
                  key={`a-${item.id}`}
                  className={`flex items-center gap-2 px-2 py-2 border-b border-steel-50 hover:bg-steel-50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`}
                >
                  <div className="w-1 self-stretch rounded-full flex-shrink-0"
                    style={{ backgroundColor: color, minHeight: 32 }} />

                  {item.user_data?.profile_picture ? (
                    <img
                      src={item.user_data.profile_picture}
                      alt={name}
                      className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-white"
                      style={{ backgroundColor: hexRgba(color, 0.85), fontSize: 10, fontWeight: 700 }}>
                      {initials(name)}
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-700 truncate leading-tight">{name}</p>
                    <p className="text-[11px] text-slate-400 truncate leading-tight">{proj}</p>
                    {(checkIn || checkOut) && (
                      <div className="flex items-center gap-1 mt-0.5">
                        {checkIn && (
                          <span className="flex items-center gap-0.5 px-1 py-0.5 rounded"
                            style={{ backgroundColor: hexRgba(TRAVEL_COLORS.check_in, 0.12), fontSize: 10, color: TRAVEL_COLORS.check_in, fontWeight: 600 }}>
                            <PlaneLanding size={9} />
                            {fmtDate(checkIn.start_date)}
                          </span>
                        )}
                        {checkOut && (
                          <span className="flex items-center gap-0.5 px-1 py-0.5 rounded"
                            style={{ backgroundColor: hexRgba(TRAVEL_COLORS.check_out, 0.12), fontSize: 10, color: TRAVEL_COLORS.check_out, fontWeight: 600 }}>
                            {fmtDate(checkOut.start_date)}
                            <PlaneTakeoff size={9} />
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="w-36 flex-shrink-0 text-center">
                    <p className="text-[11px] text-slate-500 whitespace-nowrap">
                      {fmtDate(item.start_date)} – {fmtDate(item.end_date)}
                    </p>
                  </div>
                </div>
              );
            })}

            {unmatched.length > 0 && (
              <>
                <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 border-y border-amber-100">
                  <span className="text-[10px] font-semibold text-amber-500 uppercase tracking-wide">Unassigned travel</span>
                </div>
                {unmatched.map((t, i) => {
                  const color = t.travel_type === 'check_in' ? TRAVEL_COLORS.check_in : TRAVEL_COLORS.check_out;
                  const Icon  = t.travel_type === 'check_in' ? PlaneLanding : PlaneTakeoff;
                  return (
                    <div key={`t-${t.id}`}
                      className={`flex items-center gap-2 px-2 py-2 border-b border-steel-50 hover:bg-steel-50 ${i % 2 === 0 ? '' : 'bg-slate-50/30'}`}>
                      <div className="w-1 self-stretch rounded-full flex-shrink-0"
                        style={{ backgroundColor: color, minHeight: 32 }} />
                      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: hexRgba(color, 0.15) }}>
                        <Icon size={13} style={{ color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-slate-700 truncate leading-tight">{t.user || '—'}</p>
                        <p className="text-[11px] text-slate-400">{t.travel_type === 'check_in' ? 'Check-in' : 'Check-out'}</p>
                      </div>
                      <div className="w-36 flex-shrink-0 text-center">
                        <p className="text-[11px] text-slate-500 whitespace-nowrap">
                          {fmtDate(t.start_date)}
                          {t.end_date && t.end_date !== t.start_date ? ` – ${fmtDate(t.end_date)}` : ''}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniResourceTimeline;
